import React, { useState, useEffect, useRef } from "react";
import './Css/Quotation.css';
import './Css/Quotation-responsive.css';

import { useNavigate, useLocation } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import TextInput from "../../Components/TextInput";
import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";
import { getLoginUserRoleName, UserRoleName } from "../../Utils/LoginUserRoleName";

import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Quotation as QuotationType } from "../../Controllers/Types/Quotation/QuotationType";
import { QuotationTemplate as QuotationTemplateType } from "../../Controllers/Types/Quotation/QuotationTemplate";
import { fetchQuotationData, fetchQuotationTemplates } from "../../Controllers/Thunks/QuotationThunks";
import PageLoader from "../../Components/PageLoader";
import { createQuotationProjectReference, updateQuotationProjectReference, updateQuotationProjectReferenceDetails } from "../../Api/Quotation/QuotationApiService";
import { checkText } from "../../Utils/Validator";
import { MenuBarPermission } from "../../Controllers/Types/NewPermission/MenuBarPermission";
import SharedByAvatar from "../../Components/SharedByAvatar";

const Quotation: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);
    const [createLoading, setCreateLoading] = useState<boolean>(false);

    const loadingQuotation: boolean = useAppSelector((state) => state?.quotationList?.loadingQuotation);
    const quotations: QuotationType[] = useAppSelector((state) => state?.quotationList?.quotations);
    const errorQuotation: string | null = useAppSelector((state) => state?.quotationList?.errorQuotation);

    const loadingQuotationTemplate: boolean = useAppSelector((state) => state?.quotationList?.loadingQuotationTemplate);
    const quotationTemplates: QuotationTemplateType[] = useAppSelector((state) => state?.quotationList?.quotationTemplates);
    const errorQuotationTemplate: string | null = useAppSelector((state) => state?.quotationList?.errorQuotationTemplate);

    const [quotationData, setQuotationData] = useState<QuotationType[]>([]);
    const [selectedQuotation, setSelectedQuotation] = useState<QuotationType | null>(null);

    const [quotationTypeState, setQuotationTypeState] = useState<'Own' | 'Shared'>('Own');
    const [showQuotationModal, setShowQuotationModal] = useState<boolean>(false);
    const [projectAddress, setProjectAddress] = useState<string>('');
    const [reference, setReference] = useState<string>('');

    const [projectAddressError, setProjectAddressError] = useState<string>('');
    const [referenceError, setReferenceError] = useState<string>('');

    const [quotationTemplatesData, setQuotationTemplatesData] = useState<QuotationTemplateType[]>([]);
    const [selectedQuotationTemplate, setSelectedQuotationTemplate] = useState<QuotationTemplateType | null>(null);

    const [showQuotationOptionModal, setShowQuotationOptionModal] = useState<boolean>(false);

    const [quotationOptionState, setQuotationOptionState] = useState<'Custom' | 'Template' | null>(null);

    const menuPermission: MenuBarPermission | null = location?.state?.menuPermission;

    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    // Get logged in user role name
    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };
        fetchUserRole();
    }, []);

    useEffect(() => {
        dispatch(fetchQuotationData());
        dispatch(fetchQuotationTemplates());
    }, [dispatch]);


    useEffect(() => {
        if (menuPermission && menuPermission?.have_feature) {
            setQuotationTypeState("Own");
        } else {
            setQuotationTypeState("Shared");
        }
    }, [menuPermission]);


    useEffect(() => {
        if (quotations && quotations?.length > 0) {
            let filteredData: QuotationType[] = [];

            filteredData = quotations?.filter(quotation => quotation?.type === quotationTypeState)

            setQuotationData(filteredData);
        } else {
            setQuotationData([]);
        }

        if (quotationTemplates && quotationTemplates?.length > 0) {
            let activeData: QuotationTemplateType[] = [];

            activeData = quotationTemplates?.filter(quotationtemplate => quotationtemplate?.status === 1);

            setQuotationTemplatesData(activeData);
        } else {
            setQuotationTemplatesData([]);
        }
    }, [quotations, quotationTypeState, quotationTemplates]);

    const handleChangeQuotationTypeState = (type: 'Own' | 'Shared') => {
        setQuotationTypeState(type);
    }

    /* For quotation */
    const handleCloseQuotationModal = () => {
        setShowQuotationModal(false);
        handleResetQuotationFields();
        handleResetQuotationErrors();
        setSelectedQuotation(null);
    }

    const handleAddQuotation = () => {
        setDataState('Add');
        setShowQuotationModal(true);
    }


    // project reference validation here.
    function checkCredentials(): boolean {
        const address = checkText(projectAddress, true, setProjectAddressError);
        const refe = checkText(reference, true, setReferenceError);
        return address && refe;
    }

    // on chnage validation clear.
    useEffect(() => {
        checkText(projectAddress, false, setProjectAddressError);
    }, [projectAddress]);


    useEffect(() => {
        checkText(reference, false, setReferenceError);
    }, [reference]);


    // database Error set
    function showAPIErrorMaterial(keyName: string, error: string): void {
        if (keyName === "project_address") {
            setProjectAddressError(error);
        }

        if (keyName === "project_reference") {
            setReferenceError(error);
        }
    }

    // database function create
    const handleCreateQuotation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (checkCredentials()) {
            setLoading(true);
            const data = await createQuotationProjectReference(
                projectAddress,
                reference
            );
            if (data.statusCode === 200) {
                // call project dispach event
                setLoading(false);
                handleCloseQuotationModal();
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Quotation created successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                setLoading(false);
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorMaterial(key, value[0])
                ));
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error creating the quotation.",
                        life: 3000,
                    });
                }
            }
        }
    }

    const handleEditQuotation = (quotation: QuotationType) => {
        if (quotation) {
            setSelectedQuotation(quotation);
            setDataState('Edit');
            setShowQuotationModal(true);

            setProjectAddress(quotation?.project_address || '');
            setReference(quotation?.project_reference || '');
        }
    }

    // database Update function calling here.
    const handleUpdateQuotation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (checkCredentials()) {
            setLoading(true);
            const data = await updateQuotationProjectReference(
                selectedQuotation?.id!,
                projectAddress,
                reference,
            );
            if (data.statusCode === 200) {
                // call project dispach event
                setLoading(false);
                handleCloseQuotationModal();
                dispatch(fetchQuotationData());
                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Quotation updated successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                setLoading(false);
                Object.entries(data.errors ?? []).map(([key, value], index) => (
                    showAPIErrorMaterial(key, value[0])
                ));
            } else {
                setLoading(false);
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error updateing the quotation.",
                        life: 3000,
                    });
                }
            }
        }
    }

    const handleResetQuotationFields = () => {
        setProjectAddress('');
        setReference('')
    }

    const handleResetQuotationErrors = () => {
        setProjectAddressError('');
        setReferenceError('');
    }

    const tableActionBody = (rowData: QuotationType) => {
        const quotation = rowData;
        const hasQuotationData: boolean = (quotation?.quotation_data && quotation?.quotation_data?.length > 0) ? true : false;

        return (
            <div className={`table_action_btn_area justify-content-end mt-1 mb-1`}>

                {(rowData.type === "Own") && (<button
                    className="table_action_btn p-ripple"
                    onClick={() => handleAddQuotationDetail(quotation)}
                    disabled={hasQuotationData === true}
                >
                    Add
                    <Ripple />
                </button>
                )}

                {(rowData.type === "Own") && (<button
                    className="table_action_btn p-ripple"
                    onClick={() => handleEditQuotation(quotation)}
                >
                    Edit
                    <Ripple />
                </button>)}


                <button
                    className="table_action_btn p-ripple"
                    onClick={() => handleViewQuotation(quotation)}
                    disabled={hasQuotationData === false}
                >
                    View
                    <Ripple />
                </button>
            </div>
        )
    }


    const sharedContactBody = (rowData: QuotationType) => {
        return (
            <SharedByAvatar
                avatarKey={rowData?.shared_by?.id}
                firstName={rowData?.shared_by?.first_name}
                lastName={rowData?.shared_by?.last_name}
                profileImage={rowData?.shared_by?.profile_image}
            />
        );
    };

    const quotationModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} quotation
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseQuotationModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const quotationModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseQuotationModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateQuotation : handleUpdateQuotation}
                loading={loading} className="custom_btn primary"
            />
        </div>
    );

    const handleViewQuotation = (quotation: QuotationType) => {
        navigate(`list`, {
            state: {
                quotation: quotation
            }
        })
    }


    /* For quotation detail */
    const handleCloseQuotationOptionModal = () => {
        setShowQuotationOptionModal(false);
        setSelectedQuotation(null);

        setQuotationOptionState(null);
        setSelectedQuotationTemplate(null);
    }

    const handleAddQuotationDetail = (quotation: QuotationType) => {
        setSelectedQuotation(quotation);
        setShowQuotationOptionModal(true);
    }

    const handleSelectCustomQuotation = () => {
        setQuotationOptionState('Custom');
        setSelectedQuotationTemplate(null);
    }

    const handleSelectQuotationTemplate = (template: QuotationTemplateType) => {
        setQuotationOptionState('Template');
        setSelectedQuotationTemplate(template);
    }

    const handlePreviewQuotationTemplate = () => {
        if (selectedQuotationTemplate) {
            navigate(`template`, {
                state: {
                    template: selectedQuotationTemplate,
                    quotation: selectedQuotation
                }
            });
            goToTop();
        }
    }


    // Database function for create quotation with custom options
    const handleCreateQuotationDetail = async (e: React.FormEvent) => {
        e.preventDefault();
        const data = await updateQuotationProjectReferenceDetails(
            selectedQuotation?.id!,
            quotationOptionState!,
            selectedQuotationTemplate?.id!
        );

        if (data.statusCode === 200) {
            // call project dispach event
            setCreateLoading(true);
            dispatch(fetchQuotationData());
            navigate(`list`, {
                state: {
                    quotation: selectedQuotation
                }
            })
            /*  */

            setCreateLoading(false);
            handleCloseQuotationOptionModal();

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Created successfully.',
                    life: 3000
                });
            }
        } else {
            setCreateLoading(false);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the quotation.',
                    life: 3000
                });
            }
        }
    }

    const quotationOptionModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Choose option
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseQuotationOptionModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const quotationOptionModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseQuotationOptionModal}
            />

            <Button
                label={createLoading ? 'Processing' : quotationOptionState === 'Custom' ? 'Create' : 'Preview'}
                onClick={quotationOptionState === 'Custom' ? handleCreateQuotationDetail : handlePreviewQuotationTemplate}
                loading={createLoading}
                className="custom_btn primary"
                disabled={quotationOptionState === null}
                icon={quotationOptionState === 'Template' && 'bi bi-box-arrow-up-right'}
                iconPos="right"
            />
        </div>
    );

    /* ***************************** Common function ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingQuotation || loadingQuotationTemplate ? (
                    <PageLoader />
                ) : (errorQuotation || errorQuotationTemplate) ? (
                    <ErrorPage errorMessage={errorQuotation! || errorQuotationTemplate!} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Quotation <span className="text_empty">- Projects</span></h4>
                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                {(menuPermission && menuPermission.have_feature) && (<div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${quotationTypeState === "Own" ? "active" : ""} p-ripple`}
                                        onClick={() => handleChangeQuotationTypeState('Own')}
                                    >
                                        My Quotation
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>)}


                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${quotationTypeState === "Shared" ? "active" : ""} p-ripple`}
                                        onClick={() => handleChangeQuotationTypeState('Shared')}
                                    >
                                        Shared Quotation
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>
                            </div>

                            {(menuPermission && menuPermission.have_feature) && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddQuotation}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add Quotation</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section p-0">
                            {quotationData && quotationData?.length > 0 ? (
                                <DataTable
                                    value={quotationData}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    size="small"
                                    tableStyle={{ minWidth: "50rem" }}
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                >
                                    <Column
                                        header="Quotation no."
                                        headerClassName="text_purple"
                                        body={(rowData: QuotationType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.quotation_no ? String(rowData?.quotation_no).padStart(2, '0') : '---'}.
                                            </span>
                                        )}
                                        style={{ width: "10%" }}
                                    ></Column>

                                    <Column
                                        header="Date"
                                        headerClassName="text_purple"
                                        body={(rowData: QuotationType) => (
                                            <span className="text_no_wrap">
                                                {rowData?.date ? rowData?.date : '---'}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Project address"
                                        headerClassName="text_purple"
                                        body={(rowData: QuotationType) => (
                                            <span className="note_column">
                                                {rowData?.project_address ? rowData?.project_address : '---'}
                                            </span>
                                        )}
                                        style={{ width: "25%" }}
                                    ></Column>

                                    <Column
                                        header="Reference"
                                        headerClassName="text_purple"
                                        body={(rowData: QuotationType) => (
                                            <span className="note_column">
                                                {rowData?.project_reference ? rowData?.project_reference : '---'}
                                            </span>
                                        )}
                                        style={{ width: "20%" }}
                                    ></Column>

                                    {quotationTypeState === 'Shared' && (
                                        <Column
                                            body={sharedContactBody}
                                            header="Shared by"
                                            headerClassName="text_purple"
                                            alignHeader={'center'}
                                            style={{ width: "10%" }}
                                        ></Column>
                                    )}

                                    <Column
                                        body={tableActionBody}
                                        className="table_btn_area"
                                        style={{ width: "20%" }}
                                        frozen={window.innerWidth > 575}
                                        alignFrozen="right"
                                    ></Column>

                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No quotation data found!`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Quotation modal */}
            <Dialog
                visible={showQuotationModal}
                header={quotationModalHeader}
                footer={quotationModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseQuotationModal}
                draggable={false}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-4 pt-2 pt-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <TextInput
                                    id="projectAddress"
                                    label="Project address"
                                    labelHtmlFor="projectAddress"
                                    required={true}
                                    value={projectAddress}
                                    placeholder="Project address"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectAddress(e.target.value)}
                                    error={projectAddressError}
                                />
                            </div>

                            <div className="col-12">
                                <TextInput
                                    id="reference"
                                    label="Reference"
                                    labelHtmlFor="reference"
                                    required={true}
                                    value={reference}
                                    placeholder="Reference"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReference(e.target.value)}
                                    error={referenceError}
                                    formGroupClassName="mb-sm-4 mb-2"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Quotation option modal */}
            <Dialog
                visible={showQuotationOptionModal}
                header={quotationOptionModalHeader}
                footer={quotationOptionModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_xsm"
                onHide={handleCloseQuotationOptionModal}
                dismissableMask
                draggable={false}
            >
                <div className="custom_modal_body">
                    <div className="custom_option_btn_area mb-3">
                        <button className={`custom_pc_option_btn option ${quotationOptionState === 'Custom' ? 'selected' : ''} p-ripple`}
                            onClick={handleSelectCustomQuotation}>
                            <span>
                                <i className="bi bi bi-pencil-square me-2"></i>
                                Custom
                            </span>
                            <i className={`bi ${quotationOptionState === 'Custom' ? 'bi-check-circle' : 'bi-chevron-right'}`}></i>
                            <Ripple />
                        </button>
                    </div>

                    <label className="custom_form_label highlight mb-2">Templates:</label>

                    {quotationTemplatesData && quotationTemplatesData?.length > 0 ? (
                        <div className="custom_option_btn_area">
                            {quotationTemplatesData?.map((template) => {
                                const isTemplateSelected: boolean = (quotationOptionState === 'Template' && selectedQuotationTemplate?.id === template?.id) ? true : false;

                                return (
                                    <button
                                        key={template?.id}
                                        className={`custom_pc_option_btn option ${isTemplateSelected ? 'selected' : ''} p-ripple`}
                                        onClick={() => handleSelectQuotationTemplate(template)}
                                    >
                                        <span>
                                            <i className="bi bi-ui-checks-grid me-2"></i>
                                            {template?.quotation_template_name}
                                        </span>
                                        <i className={`bi ${isTemplateSelected ? 'bi-check-circle' : 'bi-chevron-right'}`}></i>
                                        <Ripple />
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="feature_not_available_area custom">
                            <h6>
                                <i className="bi bi-exclamation-circle me-1"></i>
                                No templates available.
                            </h6>
                        </div>
                    )}
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default Quotation;